// Utility functions for exam-related operations

// Get exam-specific color scheme
export const getExamColors = (examId) => {
  const colorSchemes = {
    cat: {
      primary: '#3B82F6',
      secondary: '#1E40AF',
      accent: '#DBEAFE',
      text: '#1E3A8A'
    },
    gate: {
      primary: '#10B981',
      secondary: '#059669',
      accent: '#D1FAE5',
      text: '#065F46'
    },
    jee: {
      primary: '#8B5CF6',
      secondary: '#7C3AED',
      accent: '#EDE9FE',
      text: '#5B21B6'
    },
    neet: {
      primary: '#EF4444',
      secondary: '#DC2626',
      accent: '#FEE2E2',
      text: '#991B1B'
    },
    upsc: {
      primary: '#F97316',
      secondary: '#EA580C',
      accent: '#FED7AA',
      text: '#9A3412'
    },
    banking: {
      primary: '#6366F1',
      secondary: '#4F46E5',
      accent: '#E0E7FF',
      text: '#3730A3'
    }
  };
  
  return colorSchemes[examId] || colorSchemes.cat;
};

// Get exam-specific subjects
export const getExamSubjects = (examId) => {
  const subjects = {
    cat: [
      'Quantitative Aptitude',
      'Verbal Ability',
      'Data Interpretation',
      'Logical Reasoning'
    ],
    gate: [
      'Engineering Mathematics',
      'General Aptitude',
      'Computer Science',
      'Programming'
    ],
    jee: [
      'Physics',
      'Chemistry',
      'Mathematics'
    ],
    neet: [
      'Physics',
      'Chemistry',
      'Biology'
    ],
    upsc: [
      'General Studies',
      'CSAT',
      'Optional Subject'
    ],
    banking: [
      'Quantitative Aptitude',
      'Reasoning Ability',
      'English Language',
      'General Awareness'
    ]
  };
  
  return subjects[examId] || subjects.cat;
};

// Get exam-specific test types
export const getExamTestTypes = (examId) => {
  const testTypes = {
    cat: [
      { id: 'full', name: 'Full Mock Test', duration: 180, questions: 100 },
      { id: 'sectional', name: 'Sectional Test', duration: 60, questions: 34 },
      { id: 'topic', name: 'Topic-wise Test', duration: 30, questions: 20 }
    ],
    gate: [
      { id: 'full', name: 'Full Mock Test', duration: 180, questions: 65 },
      { id: 'sectional', name: 'Sectional Test', duration: 45, questions: 15 },
      { id: 'topic', name: 'Topic-wise Test', duration: 30, questions: 10 }
    ],
    jee: [
      { id: 'full', name: 'Full Mock Test', duration: 180, questions: 75 },
      { id: 'sectional', name: 'Sectional Test', duration: 60, questions: 25 },
      { id: 'topic', name: 'Topic-wise Test', duration: 30, questions: 15 }
    ],
    neet: [
      { id: 'full', name: 'Full Mock Test', duration: 200, questions: 180 },
      { id: 'sectional', name: 'Sectional Test', duration: 60, questions: 45 },
      { id: 'topic', name: 'Topic-wise Test', duration: 30, questions: 20 }
    ],
    upsc: [
      { id: 'full', name: 'Full Mock Test', duration: 240, questions: 100 },
      { id: 'sectional', name: 'Sectional Test', duration: 180, questions: 80 },
      { id: 'topic', name: 'Topic-wise Test', duration: 60, questions: 40 }
    ],
    banking: [
      { id: 'full', name: 'Full Mock Test', duration: 140, questions: 200 },
      { id: 'sectional', name: 'Sectional Test', duration: 30, questions: 35 },
      { id: 'topic', name: 'Topic-wise Test', duration: 20, questions: 20 }
    ]
  };
  
  return testTypes[examId] || testTypes.cat;
};

// Format exam name for display
export const formatExamName = (examId) => {
  const names = {
    cat: 'CAT',
    gate: 'GATE',
    jee: 'JEE',
    neet: 'NEET',
    upsc: 'UPSC',
    banking: 'Banking'
  };
  
  return names[examId] || 'CAT';
};

// Get exam full name
export const getExamFullName = (examId) => {
  const fullNames = {
    cat: 'Common Admission Test',
    gate: 'Graduate Aptitude Test in Engineering',
    jee: 'Joint Entrance Examination',
    neet: 'National Eligibility cum Entrance Test',
    upsc: 'Union Public Service Commission',
    banking: 'Banking Examinations'
  };
  
  return fullNames[examId] || 'Common Admission Test';
};

// Validate exam ID
export const isValidExamId = (examId) => {
  const validExams = ['cat', 'gate', 'jee', 'neet', 'upsc', 'banking'];
  return validExams.includes(examId);
};

// Get exam difficulty levels
export const getExamDifficultyLevels = (examId) => {
  const levels = {
    cat: ['Easy', 'Medium', 'Hard', 'Expert'],
    gate: ['Easy', 'Medium', 'Hard'],
    jee: ['Easy', 'Medium', 'Hard', 'Expert'],
    neet: ['Easy', 'Medium', 'Hard'],
    upsc: ['Easy', 'Medium', 'Hard'],
    banking: ['Easy', 'Medium', 'Hard']
  };
  
  return levels[examId] || levels.cat;
};

// Calculate exam-specific target scores
export const getExamTargetScores = (examId) => {
  const targets = {
    cat: { excellent: 95, good: 85, average: 75, minimum: 60 },
    gate: { excellent: 90, good: 80, average: 70, minimum: 50 },
    jee: { excellent: 95, good: 85, average: 75, minimum: 60 },
    neet: { excellent: 95, good: 85, average: 75, minimum: 50 },
    upsc: { excellent: 85, good: 75, average: 65, minimum: 50 },
    banking: { excellent: 90, good: 80, average: 70, minimum: 60 }
  };
  
  return targets[examId] || targets.cat;
};

// Function to get comprehensive exam data for Firebase
export const getComprehensiveExamData = () => {
  return {
    cat: {
      name: 'CAT',
      fullName: 'Common Admission Test',
      description: 'Management entrance exam for IIMs',
      color: 'bg-blue-500',
      textColor: 'text-blue-600',
      borderColor: 'border-blue-200',
      icon: '🎓',
      category: 'management'
    },
    gate: {
      name: 'GATE',
      fullName: 'Graduate Aptitude Test in Engineering',
      description: 'Engineering postgraduate entrance exam',
      color: 'bg-green-500',
      textColor: 'text-green-600',
      borderColor: 'border-green-200',
      icon: '⚙️',
      category: 'engineering'
    },
    jee: {
      name: 'JEE',
      fullName: 'Joint Entrance Examination',
      description: 'Engineering undergraduate entrance exam',
      color: 'bg-purple-500',
      textColor: 'text-purple-600',
      borderColor: 'border-purple-200',
      icon: '🔬',
      category: 'engineering'
    },
    neet: {
      name: 'NEET',
      fullName: 'National Eligibility cum Entrance Test',
      description: 'Medical entrance examination',
      color: 'bg-red-500',
      textColor: 'text-red-600',
      borderColor: 'border-red-200',
      icon: '🏥',
      category: 'medical'
    },
    upsc: {
      name: 'UPSC',
      fullName: 'Union Public Service Commission',
      description: 'Civil services examination',
      color: 'bg-orange-500',
      textColor: 'text-orange-600',
      borderColor: 'border-orange-200',
      icon: '🏛️',
      category: 'civil_services'
    },
    banking: {
      name: 'Banking',
      fullName: 'Banking Examinations',
      description: 'IBPS, SBI, and other banking exams',
      color: 'bg-indigo-500',
      textColor: 'text-indigo-600',
      borderColor: 'border-indigo-200',
      icon: '🏦',
      category: 'banking'
    },
    ssc_cgl: {
      name: 'SSC-CGL',
      fullName: 'Staff Selection Commission Combined Graduate Level',
      description: 'Government job examination for graduates',
      color: 'bg-teal-500',
      textColor: 'text-teal-600',
      borderColor: 'border-teal-200',
      icon: '👔',
      category: 'government'
    },
    ssc_chsl: {
      name: 'SSC-CHSL',
      fullName: 'Staff Selection Commission Combined Higher Secondary Level',
      description: 'Government job examination for 12th pass',
      color: 'bg-cyan-500',
      textColor: 'text-cyan-600',
      borderColor: 'border-cyan-200',
      icon: '📋',
      category: 'government'
    },
    rrb_ntpc: {
      name: 'RRB NTPC',
      fullName: 'Railway Recruitment Board Non-Technical Popular Categories',
      description: 'Railway recruitment examination',
      color: 'bg-amber-500',
      textColor: 'text-amber-600',
      borderColor: 'border-amber-200',
      icon: '🚂',
      category: 'railway'
    },
    ctet: {
      name: 'CTET',
      fullName: 'Central Teacher Eligibility Test',
      description: 'Teacher eligibility examination',
      color: 'bg-emerald-500',
      textColor: 'text-emerald-600',
      borderColor: 'border-emerald-200',
      icon: '📚',
      category: 'teaching'
    },
    net: {
      name: 'NET',
      fullName: 'National Eligibility Test',
      description: 'Assistant Professor and JRF examination',
      color: 'bg-violet-500',
      textColor: 'text-violet-600',
      borderColor: 'border-violet-200',
      icon: '🎓',
      category: 'academic'
    },
    clat: {
      name: 'CLAT',
      fullName: 'Common Law Admission Test',
      description: 'Law entrance examination',
      color: 'bg-slate-500',
      textColor: 'text-slate-600',
      borderColor: 'border-slate-200',
      icon: '⚖️',
      category: 'law'
    },
    ailet: {
      name: 'AILET',
      fullName: 'All India Law Entrance Test',
      description: 'National Law University entrance exam',
      color: 'bg-stone-500',
      textColor: 'text-stone-600',
      borderColor: 'border-stone-200',
      icon: '🏛️',
      category: 'law'
    },
    nda: {
      name: 'NDA',
      fullName: 'National Defence Academy',
      description: 'Defence services entrance examination',
      color: 'bg-sky-500',
      textColor: 'text-sky-600',
      borderColor: 'border-sky-200',
      icon: '🎖️',
      category: 'defence'
    },
    cds: {
      name: 'CDS',
      fullName: 'Combined Defence Services',
      description: 'Defence services examination',
      color: 'bg-blue-600',
      textColor: 'text-blue-700',
      borderColor: 'border-blue-300',
      icon: '🛡️',
      category: 'defence'
    }
  };
};
